// iap.tsx — Apple In-App Purchase wallet top-up (iOS only). Per-app copy; only PRODUCT_PREFIX differs.
//
// Each tier shows what you GET (Oriora credit) and what you PAY (live Apple price from get_products).
// Tap → StoreKit purchase → signed JWS receipt → POST /iap/apple/verify → credits the Apple-tagged
// balance (source=apple_iap, separate from Creem). iOS-only. The buy-sheet shows credit AMOUNTS, never
// restates Apple's price as our own (native-surface price-rule + Apple anti-steering).
import { useEffect, useState } from "react";
import { invoke } from "./tauri-web";
import { getAccessToken } from "./supabase";

const SERVER = "https://api.orioralabs.com";
const ACCOUNT_URL = "https://orioralabs.com/settings?tab=wallet";
const APPS_URL = "https://orioralabs.com/apps";
const PRODUCT_PREFIX = "com.orioralabs.skein.wallet";

export const CREDIT_TIERS = [20, 30, 50, 80, 100, 150, 200, 250] as const;
const productIdFor = (usd: number) => `${PRODUCT_PREFIX}.${usd}`;

type IapProduct = { id: string; title: string; description: string; price: string; priceValue: number };
type PurchaseResult = { transactionId: string; jwsRepresentation: string; productId: string };

export async function buyCredit(usd: number): Promise<void> {
  const result = await invoke<PurchaseResult>("plugin:iap|purchase", { productId: productIdFor(usd) });
  const token = await getAccessToken();
  if (!token) throw new Error("Sign in to Oriora first.");
  const res = await fetch(`${SERVER}/iap/apple/verify`, {
    method: "POST",
    headers: { "content-type": "application/json", authorization: `Bearer ${token}` },
    body: JSON.stringify({ jws_representation: result.jwsRepresentation }),
  });
  if (!res.ok) throw new Error("Purchased — but the top-up hasn't applied yet. Contact support if your balance doesn't update.");
}

function openExternal(url: string) {
  void invoke("open_external", { url });
}

const linkStyle: React.CSSProperties = {
  background: "none", border: "none", padding: 0, textAlign: "left",
  cursor: "pointer", fontSize: "11px", color: "#0d9488", fontWeight: 500,
};

export function WalletTopUp() {
  const [busy, setBusy] = useState<number | null>(null);
  const [msg, setMsg] = useState<string | null>(null);
  const [prices, setPrices] = useState<Record<string, string>>({});

  useEffect(() => {
    let alive = true;
    invoke<IapProduct[]>("plugin:iap|get_products", { productIds: CREDIT_TIERS.map(productIdFor) })
      .then((ps) => { if (alive) setPrices(Object.fromEntries(ps.map((p) => [p.id, p.price]))); })
      .catch(() => {});
    return () => { alive = false; };
  }, []);

  const topUp = async (usd: number) => {
    // Gate on a LOADED price: the SK2 purchase path must only run when the store already
    // proved it can resolve products (unresolvable products crash SK2's async machinery).
    if (!prices[productIdFor(usd)]) {
      setMsg("Store prices haven't loaded yet — check your connection and try again in a moment.");
      return;
    }
    setBusy(usd);
    setMsg(null);
    try {
      await buyCredit(usd);
      setMsg(`Added ${usd} to your Oriora balance.`);
    } catch (e) {
      setMsg(e instanceof Error ? e.message : "Couldn't complete the purchase.");
    } finally {
      setBusy(null);
    }
  };

  return (
    <div style={{ marginTop: "0.85rem", paddingTop: "0.75rem", borderTop: "1px solid var(--border)" }}>
      <div style={{ fontSize: "var(--fs-sm, 13px)", fontWeight: 600, marginBottom: 2 }}>Add funds</div>
      <div className="muted" style={{ fontSize: "10px", marginBottom: 10, lineHeight: 1.4 }}>
        Your Oriora balance covers AI across this and every Oriora app — add it here, spend it only as you go.
      </div>
      <div style={{ display: "grid", gridTemplateColumns: "repeat(2, 1fr)", gap: 6 }}>
        {CREDIT_TIERS.map((usd) => {
          const price = prices[productIdFor(usd)];
          return (
            <button
              key={usd}
              type="button"
              className="btn"
              disabled={busy !== null}
              onClick={() => void topUp(usd)}
              style={{ display: "flex", flexDirection: "column", alignItems: "center", gap: 1, padding: "0.5rem 0", fontSize: "var(--fs-xs)" }}
            >
              <span style={{ fontWeight: 700 }}>{busy === usd ? "…" : `+${usd}`}</span>
              <span className="muted" style={{ fontSize: "9px" }}>
                {busy === usd ? "" : price ? `Pay ${price}` : "funds"}
              </span>
            </button>
          );
        })}
      </div>
      {msg && (
        <div className="muted" style={{ fontSize: "10px", marginTop: 8, lineHeight: 1.4 }}>
          {msg}
        </div>
      )}
      <div style={{ marginTop: 12, display: "flex", flexDirection: "column", gap: 7 }}>
        <button type="button" style={linkStyle} onClick={() => openExternal(ACCOUNT_URL)}>
          See your balance &amp; manage your account →
        </button>
        <button type="button" style={linkStyle} onClick={() => openExternal(APPS_URL)}>
          Your balance works in every Oriora app — see them all →
        </button>
      </div>
    </div>
  );
}
